import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainA from "../../layout/Main Admin";
import Loader from "../../components/atoms/Loader";
import Button from "../../components/atoms/Button";
import Anchor from "../../components/atoms/Anchor";
import Modal from "../../components/atoms/Modal";
import Input from "../../components/atoms/Input";
import Upload from "../../components/atoms/Upload";
import ToggleSwitch from "../../components/atoms/ToggleSwitch";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { getRequest, postRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import edit from "../../assets/icons/edit.svg";

import "./styles.scss";

const baseUrl =  "https://dynamicproductstorage.blob.core.windows.net/form/";

const defaultField = {
  FormFieldId: 0,
  FormId: 0,
  Label: "",
  FieldName: "",
  FieldType: "text",
  IsRequired: true,
  ValidationJson: "",
  Options: "",
  Placeholder: "",
  DefaultValue: "",
  OrderIndex: 0,
  CreatedBy: "",
};

const getDefaultValidationForType = (type) => {
  const t = (type || "").toString().toLowerCase();
  switch (t) {
    case "textarea":
      return {
        minLength: 10,
        maxLength: 500,
        trim: true,
      };
    case "select":
      return {
        enum: true,
      };
    case "checkbox":
      return {
        boolean: true,
      };
    case "radio":
      return {
        enum: true,
      };
    case "number":
      return {
        numeric: true,
        min: 0,
        max: 1000000000,
        noLeadingZeros: false,
      };
    case "email":
      return {
        format: "email",
        maxLength: 100,
      };
    case "date":
      return {
        format: "YYYY-MM-DD",
        minDate: null,
        maxDate: null,
      };
    case "time":
      return {
        format: "HH:mm",
        minTime: null,
        maxTime: null,
      };
    case "datetime":
    case "date time":
      return {
        format: "ISO",
        min: null,
        max: null,
      };
    case "file":
      return {
        allowedTypes: ["pdf", "jpg", "jpeg", "png"],
        maxSize: 5 * 1024 * 1024,
        virusScan: true,
      };
    case "tel":
      return {
        digitsOnly: true,
        allowPlus: true,
        minLength: 10,
        maxLength: 15,
      };
    case "text":
    default:
      return {
        minLength: 2,
        maxLength: 100,
        trim: true,
        disallowOnlySpecial: false,
      };
  }
};

const FormFields = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [field, setField] = useState(defaultField);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    message: "",
    onConfirm: null,
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (formId) fetchFields(formId);
  }, [formId]);

  const fetchFields = async (id) => {
    setLoading(true);
    try {
      const resp = await getRequest(`FormField/GetByForm/${id}`);
      if (resp?.status === 200) {
        let fieldsData = Array.isArray(resp.data) ? resp.data : [];

        // fetch field options and map into fields
        try {
          const optResp = await getRequest(
            `FieldOption/GetFieldOptionByFormId/${id}`,
          );
          if (optResp?.status === 200 && Array.isArray(optResp.data)) {
            const map = {};
            optResp.data.forEach((o) => {
              const fid = o.FormFieldId ?? o.formFieldId ?? 0;
              if (!map[fid]) map[fid] = [];
              map[fid].push({
                FieldOptionId: o.fieldOptionId ?? o.FieldOptionId ?? 0,
                FormFieldId: fid,
                OptionLabel:
                  o.optionLabel ??
                  o.OptionLabel ??
                  o.Option ??
                  o.Value ??
                  String(o.optionLabel ?? o.Option ?? ""),
                OptionValue:
                  o.optionValue ??
                  o.OptionValue ??
                  o.Value ??
                  o.option ??
                  String(o.optionValue ?? ""),
                OrderIndex: o.orderIndex ?? o.OrderIndex ?? 0,
              });
            });

            fieldsData = fieldsData.map((f) => {
              const fid = f.formFieldId ?? f.FormFieldId ?? 0;
              const opts = (map[fid] || [])
                .slice()
                .sort(
                  (a, b) =>
                    (a.OrderIndex ?? a.orderIndex ?? 0) -
                    (b.OrderIndex ?? b.orderIndex ?? 0),
                );
              const optionsArray = opts.map((x) => ({ ...x }));
              const optionsCsv = optionsArray
                .map((o) => o.OptionValue ?? o.OptionLabel)
                .filter(Boolean)
                .join(",");
              return {
                ...f,
                Options: optionsCsv,
                OptionsArray: optionsArray,
                OriginalOptionIds: optionsArray.map((o) => o.FieldOptionId),
              };
            });
          } else {
            fieldsData = fieldsData.map((f) => {
              const raw = (f.Options ?? f.options ?? "")
                .toString()
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
              const opts = raw.map((v, i) => ({
                FieldOptionId: 0,
                FormFieldId: f.formFieldId ?? f.FormFieldId ?? 0,
                OptionLabel: v,
                OptionValue: v,
                OrderIndex: i,
              }));
              return {
                ...f,
                Options: raw.join(","),
                OptionsArray: opts,
                OriginalOptionIds: opts.map((o) => o.FieldOptionId),
              };
            });
          }
        } catch (err) {
          // if options fetch fails, still set fields with whatever we have
          fieldsData = fieldsData.map((f) => {
            const raw = (f.Options ?? f.options ?? "")
              .toString()
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            const opts = raw.map((v, i) => ({
              FieldOptionId: 0,
              FormFieldId: f.formFieldId ?? f.FormFieldId ?? 0,
              OptionLabel: v,
              OptionValue: v,
              OrderIndex: i,
            }));
            return {
              ...f,
              Options: raw.join(","),
              OptionsArray: opts,
              OriginalOptionIds: opts.map((o) => o.FieldOptionId),
            };
          });
        }

        // normalize order and sort by OrderIndex so UI respects saved order
        fieldsData = (fieldsData || [])
          .slice()
          .sort(
            (a, b) =>
              (a.orderIndex ?? a.OrderIndex ?? 0) -
              (b.orderIndex ?? b.OrderIndex ?? 0),
          )
          .map((f, idx) => ({
            ...f,
            OrderIndex: f.orderIndex ?? f.OrderIndex ?? idx,
          }));
        setFields(fieldsData);
      } else {
        setFields([]);
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to fetch form fields");
    } finally {
      setLoading(false);
    }
  };

  const renderPreviewField = (f, idx) => {
    const type = (f.FieldType ?? f.fieldType ?? "").toString().toLowerCase();
    const val = f.DefaultValue ?? f.defaultValue ?? "";
    const options = f.OptionsArray || [];

    const label = <label className="preview-label">{f.Label || f.label}</label>;

    switch (type) {
      case "textarea":
        return (
          <div className="preview-row" key={idx}>
            {label}
            <textarea defaultValue={val} disabled />
          </div>
        );
      case "select":
        return (
          <div className="preview-row" key={idx}>
            {label}
            <select disabled>
              <option value="">-- select --</option>
              {options.map((o, i) => (
                <option key={i} value={o.OptionValue ?? o.OptionLabel}>
                  {o.OptionLabel ?? o.OptionValue}
                </option>
              ))}
            </select>
          </div>
        );
      case "multiselect":
        return (
          <div className="preview-row" key={idx}>
            {label}
            <select multiple disabled>
              {options.map((o, i) => (
                <option key={i} value={o.OptionValue ?? o.OptionLabel}>
                  {o.OptionLabel ?? o.OptionValue}
                </option>
              ))}
            </select>
          </div>
        );
      case "checkbox":
        if (options.length > 0) {
          return (
            <div className="preview-row" key={idx}>
              {label}
              <div className="preview-multi">
                {options.map((o, i) => (
                  <label key={i} className="preview-inline">
                    <input type="checkbox" disabled />{" "}
                    {o.OptionLabel ?? o.OptionValue}
                  </label>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div className="preview-row" key={idx}>
            <label className="preview-inline">
              <input type="checkbox" disabled /> {f.Label}
            </label>
          </div>
        );
      case "radio":
        return (
          <div className="preview-row" key={idx}>
            {label}
            <div className="preview-multi">
              {options.map((o, i) => (
                <label key={i} className="preview-inline">
                  <input name={`preview-${idx}`} type="radio" disabled />{" "}
                  {o.OptionLabel ?? o.OptionValue}
                </label>
              ))}
            </div>
          </div>
        );
      case "image":
        return (
          <div className="preview-row" key={idx}>
            {label}
            <div className="preview-images">
              {options.map((o, i) => (
                <img
                  key={i}
                  src={baseUrl + o.OptionValue}
                  alt={o.OptionLabel}
                  style={{ maxWidth: 120, maxHeight: 80, marginRight: 8 }}
                />
              ))}
            </div>
          </div>
        );
      default:
        const inputType =
          type === "number" ||
          type === "email" ||
          type === "date" ||
          type === "time"
            ? type
            : "text";
        return (
          <div className="preview-row" key={idx}>
            {label}
            <input type={inputType} defaultValue={val} disabled />
          </div>
        );
    }
  };

  const openCreate = () => {
    setEditing(false);
    const user = JSON.parse(localStorage.getItem("currentUser")) || {};
    setField({
      ...defaultField,
      FormId: Number(formId),
      CreatedBy: user.userFirstName || user.userName || "Admin",
      Options: "",
      OptionsArray: [],
      ValidationJson: JSON.stringify(
        getDefaultValidationForType(defaultField.FieldType || "text"),
      ),
    });
    setShowEditor(true);
  };

  const openEdit = (f, idx) => {
    setEditing(true);
    setEditingIndex(idx);
    const fallbackOptions = (f.options ?? f.Options ?? "")
      .toString()
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const fallbackOptionsObjects = fallbackOptions.map((v, i) => ({
      FieldOptionId: 0,
      FormFieldId: f.formFieldId ?? f.FormFieldId ?? 0,
      OptionLabel: v,
      OptionValue: v,
      OrderIndex: i,
    }));

    // prefer API-provided OptionsArray if present, otherwise use fallback CSV
    let optionsForEditor = [];
    if (Array.isArray(f.OptionsArray) && f.OptionsArray.length > 0) {
      optionsForEditor = f.OptionsArray.map((o, i) => ({
        FieldOptionId: o.FieldOptionId ?? o.fieldOptionId ?? 0,
        FormFieldId:
          o.FormFieldId ?? o.formFieldId ?? f.formFieldId ?? f.FormFieldId ?? 0,
        OptionLabel:
          o.OptionLabel ?? o.optionLabel ?? o.Option ?? o.Value ?? "",
        OptionValue:
          o.OptionValue ?? o.optionValue ?? o.Value ?? o.option ?? "",
        OrderIndex: o.OrderIndex ?? o.orderIndex ?? i,
      }));
    } else {
      optionsForEditor = fallbackOptionsObjects;
    }

    // reset any transient option inputs (we now use per-option inputs)

    setField({
      FormFieldId: f.formFieldId ?? f.FormFieldId ?? 0,
      FormId: f.formId ?? f.FormId ?? Number(formId),
      Label: f.label ?? f.Label ?? "",
      FieldName: (f.label ?? f.Label ?? "").toLowerCase().replace(/\s+/g, "_"),
      FieldType: f.fieldType ?? f.FieldType ?? "text",
      ValidationJson: f.validationJson ?? f.ValidationJson ?? "",
      Options: optionsForEditor
        .map((o) => o.OptionValue ?? o.OptionLabel ?? "")
        .filter(Boolean)
        .join(","),
      OptionsArray: optionsForEditor,
      OriginalOptionIds:
        f.OriginalOptionIds &&
        Array.isArray(f.OriginalOptionIds) &&
        f.OriginalOptionIds.length > 0
          ? f.OriginalOptionIds
          : optionsForEditor.map((o) => o.FieldOptionId ?? 0),
      Placeholder: f.placeholder ?? f.Placeholder ?? "",
      DefaultValue: f.defaultValue ?? f.DefaultValue ?? "",
      IsRequired: !!(f.isRequired ?? f.IsRequired),
      OrderIndex: f.orderIndex ?? f.OrderIndex ?? 0,
      CreatedBy: f.createdBy ?? f.CreatedBy ?? "",
    });
    setTimeout(() => setShowEditor(true), 0);
  };

  // Save changes locally in modal; actual persistence happens in submitAllFields
  const localSaveField = () => {
    if (!field.Label || field.Label.trim() === "") {
      NotificationManager.error("Please enter field label");
      return;
    }

    // ensure options are serialized from OptionsArray if present
    const optionsArray = Array.isArray(field.OptionsArray)
      ? field.OptionsArray
      : [];
    const optionsCsv = optionsArray
      .map((o) =>
        (o.OptionValue ?? o.OptionLabel ?? String(o)).toString().trim(),
      )
      .filter(Boolean)
      .join(",");
    const fieldToSave = {
      ...field,
      Options: optionsCsv,
      OptionsArray: optionsArray,
    };

    setFields((prev) => {
      const next = [...prev];
      if (
        editing &&
        editingIndex !== null &&
        typeof editingIndex !== "undefined"
      ) {
        next[editingIndex] = JSON.parse(JSON.stringify(fieldToSave));
      } else {
        // append as new
        next.push({ ...fieldToSave, OrderIndex: next.length });
      }
      return next.map((p, idx) => ({ ...p, OrderIndex: idx }));
    });

    setEditing(false);
    setEditingIndex(null);
    setShowEditor(false);
  };

  const submitAllFields = async () => {
    setLoading(true);
    try {
      // process fields sequentially so we can synchronize options per-field
      for (let idx = 0; idx < fields.length; idx++) {
        const it = fields[idx];

        // keep existing ValidationJson; do NOT overwrite it with options
        let validation = it.ValidationJson ?? "";

        const payload = {
          FormFieldId: it.FormFieldId ?? it.formFieldId ?? 0,
          FormId: it.FormId ?? it.formId ?? Number(formId),
          Label: it.Label ?? it.label ?? "",
          FieldName: (it.Label ?? it.label ?? "").toLowerCase().replace(/\s+/g, "_"),
          FieldType: it.FieldType ?? it.fieldType ?? "",
          IsRequired: !!(it.IsRequired ?? it.isRequired),
          ValidationJson: validation,
          Placeholder: it.Placeholder ?? it.placeholder ?? "",
          DefaultValue: it.DefaultValue ?? it.defaultValue ?? "",
          OrderIndex: it.OrderIndex ?? it.orderIndex ?? idx,
          CreatedBy: it.CreatedBy ?? it.createdBy ?? "",
          CreatedDate:
            it.CreatedDate ?? it.createdDate ?? new Date().toISOString(),
        };

        let resp;
        if (payload.FormFieldId && payload.FormFieldId > 0) {
          resp = await postRequest("FormField/UpdateFormField", payload);
        } else {
          resp = await postRequest("FormField/CreateFormField", payload);
        }

        // determine saved formField id
        let savedFieldId = payload.FormFieldId || payload.formFieldId || 0;
        if (resp?.status === 200 || resp?.status === 201) {
          if (resp.data) {
            savedFieldId =
              resp.data.formFieldId ?? resp.data.FormFieldId ?? savedFieldId;
          }
        }

        // now synchronize options for this field
        const options = it.OptionsArray || [];
        const originalIds = it.OriginalOptionIds || [];
        const currentIds = options
          .filter((o) => o.FieldOptionId && o.FieldOptionId > 0)
          .map((o) => o.FieldOptionId);

        // delete removed options
        const toDelete = (originalIds || []).filter(
          (id) => !currentIds.includes(id),
        );
        for (const delId of toDelete) {
          try {
            await getRequest(`FieldOption/DeleteFieldOption/${delId}`);
          } catch (err) {
            console.error(`Failed to delete FieldOption ${delId}:`, err);
          }
        }

        // create or update current options (respect order)
        for (let oi = 0; oi < options.length; oi++) {
          const o = options[oi];
          const optPayload = {
            FieldOptionId: o.FieldOptionId ?? 0,
            FormFieldId: savedFieldId,
            OptionLabel: o.OptionLabel ?? o.OptionValue ?? "",
            OptionValue: o.OptionValue ?? o.OptionLabel ?? "",
            OrderIndex: oi,
          };

          try {
            if (optPayload.FieldOptionId && optPayload.FieldOptionId > 0) {
              await postRequest("FieldOption/UpdateFieldOption", optPayload);
            } else {
              await postRequest("FieldOption/CreateFieldOption", optPayload);
            }
          } catch (err) {
            console.error("Error saving field option:", err);
          }
        }
      }

      NotificationManager.success("All fields and options saved");
      fetchFields(formId);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error saving fields");
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (message, onConfirm) => {
    setConfirmModal({ isOpen: true, message, onConfirm });
  };

  const deleteField = async (f, idx) => {
    const id = f.formFieldId ?? f.FormFieldId;

    // If the field is new (no id), remove it locally after confirmation
    if (!id || id === 0) {
      openConfirm("Remove this unsaved field?", () => {
        setFields((prev) => {
          const next = prev.filter((_, i) => i !== idx);
          return next.map((p, i) => ({ ...p, OrderIndex: i }));
        });
        // close editor if it's the current unsaved field
        if (
          (field.FormFieldId ?? 0) === 0 &&
          field.Label === f.Label &&
          field.FieldName === f.FieldName
        ) {
          setShowEditor(false);
        }
        return Promise.resolve();
      });
      return;
    }

    // For saved fields, confirm then call API
    openConfirm("Are you sure you want to delete this field?", async () => {
      setLoading(true);
      try {
        const resp = await getRequest(`FormField/DeleteFormField/${id}`);
        if (resp?.status === 200) {
          NotificationManager.success("Field deleted");
          fetchFields(formId);
        } else {
          NotificationManager.error("Failed to delete field");
        }
      } catch (err) {
        console.error(err);
        NotificationManager.error("Error deleting field");
      } finally {
        setLoading(false);
      }
      return Promise.resolve();
    });
  };

  // Drag & drop helpers (using react-beautiful-dnd)
  const createFieldOfType = (type, insertIndex = null, openEditor = false) => {
    const user = JSON.parse(localStorage.getItem("currentUser")) || {};
    const base = {
      FormFieldId: 0,
      FormId: Number(formId),
      Label: `New ${type}`,
      FieldName: `new_${type.toLowerCase()}`,
      FieldType: type,
      IsRequired: true,
      ValidationJson: JSON.stringify(getDefaultValidationForType(type)),
      Placeholder: "",
      DefaultValue: "",
      OrderIndex: 0,
      CreatedBy: user.userFirstName || user.userName || "Admin",
    };

    if (insertIndex === null) {
      setFields((prev) => {
        const next = [...prev, base].map((p, idx) => ({
          ...p,
          OrderIndex: idx,
        }));
        // optionally open editor for the newly added item
        if (openEditor) {
          const idx = next.length - 1;
          setEditing(true);
          setEditingIndex(idx);
          setField(next[idx]);
          setShowEditor(true);
        }
        // do NOT persist order changes here — keep edits local until Save All
        return next;
      });
    } else {
      setFields((prev) => {
        const next = [
          ...prev.slice(0, insertIndex),
          base,
          ...prev.slice(insertIndex),
        ].map((p, idx) => ({ ...p, OrderIndex: idx }));
        if (openEditor) {
          setEditing(true);
          setEditingIndex(insertIndex);
          setField(next[insertIndex]);
          setShowEditor(true);
        }
        // do NOT persist order changes here — keep edits local until Save All
        return next;
      });
    }
  };

  const persistOrderChanges = async (items) => {
    try {
      const updates = items
        .map((it) => ({ ...it }))
        .filter(
          (it) =>
            (it.FormFieldId ?? it.formFieldId) &&
            (it.FormFieldId ?? it.formFieldId) > 0,
        )
        .map((it) => {
          const payload = {
            FormFieldId: it.FormFieldId ?? it.formFieldId,
            FormId: it.FormId ?? it.formId,
            Label: it.Label ?? it.label ?? "",
            FieldName: it.FieldName ?? it.fieldName ?? "",
            FieldType: it.FieldType ?? it.fieldType ?? "",
            IsRequired: !!(it.IsRequired ?? it.isRequired),
            ValidationJson: it.ValidationJson ?? it.validationJson ?? "",
            Placeholder: it.Placeholder ?? it.placeholder ?? "",
            DefaultValue: it.DefaultValue ?? it.defaultValue ?? "",
            OrderIndex: it.OrderIndex ?? it.orderIndex ?? 0,
            CreatedBy: it.CreatedBy ?? it.createdBy ?? "",
          };
          return postRequest("FormField/UpdateFormField", payload);
        });

      if (updates.length > 0) await Promise.all(updates);
    } catch (err) {
      console.error("Error persisting order changes:", err);
    }
  };
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    // drag from palette into canvas -> create new field at destination.index
    if (
      source.droppableId === "palette" &&
      destination.droppableId === "canvas"
    ) {
      const type = draggableId.replace(/^palette-/, "");
      createFieldOfType(type, destination.index);
      return;
    }

    // reorder within canvas
    if (
      source.droppableId === "canvas" &&
      destination.droppableId === "canvas"
    ) {
      if (source.index === destination.index) return;
      setFields((prev) => {
        const next = Array.from(prev);
        const [moved] = next.splice(source.index, 1);
        next.splice(destination.index, 0, moved);
        return next.map((p, idx) => ({ ...p, OrderIndex: idx }));
      });
      return;
    }
  };

  const validateField = (name, value, fieldObj) => {
    let err = "";

    let validation = {};
    try {
      validation = JSON.parse(fieldObj.ValidationJson || "{}");
    } catch {}

    // REQUIRED
    if (fieldObj.IsRequired && (!value || value.trim() === "")) {
      return "This field is required";
    }

    // TRIM
    if (validation.trim) {
      value = value.trim();
    }

    // MIN LENGTH
    if (validation.minLength && value.length < validation.minLength) {
      return `Minimum ${validation.minLength} characters required`;
    }

    // MAX LENGTH
    if (validation.maxLength && value.length > validation.maxLength) {
      return `Maximum ${validation.maxLength} characters allowed`;
    }

    // EMAIL
    if (validation.format === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        return "Invalid email format";
      }
    }

    // NUMBER
    if (validation.numeric) {
      if (isNaN(value)) return "Must be a number";

      if (validation.min !== null && value < validation.min) {
        return `Minimum value is ${validation.min}`;
      }

      if (validation.max !== null && value > validation.max) {
        return `Maximum value is ${validation.max}`;
      }

      if (validation.noLeadingZeros && /^0\d+/.test(value)) {
        return "No leading zeros allowed";
      }
    }

    // TEL
    if (validation.digitsOnly && !/^\+?\d+$/.test(value)) {
      return "Only digits allowed";
    }

    return err;
  };

  const handleChange = (name, value, fieldObj) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));

    const error = validateField(name, value, fieldObj);

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = () => {
    let newErrors = {};

    fields.forEach((f) => {
      const name = f.FieldName;
      const value = formValues[name] || "";

      const error = validateField(name, value, f);
      if (error) newErrors[name] = error;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      console.log("Form valid ✅");
    }
  };

  return (
    <MainA className="b-form-fields-wrapper">
      <Loader loading={loading} />

      <div className="wrapper">
        <div className="head">
          <h4>
            {showEditor
              ? editing
                ? "Edit Field"
                : "Create Field"
              : `Form Fields`}
          </h4>

          <div className="view-toggle">
            {!previewOpen && (
              <Button onClick={submitAllFields}>Save All</Button>
            )}
            <Button onClick={() => setPreviewOpen((prev) => !prev)}>
              {previewOpen ? "Hide Preview" : "Preview"}
            </Button>
            {!previewOpen && 
              <Button className="primary" onClick={() => navigate(-1)}>
                Back
              </Button>
            }
          </div>
        </div>
        {!previewOpen ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="form-builder">
              <Droppable droppableId="palette" isDropDisabled={true}>
                {(provided) => (
                  <div
                    className="palette"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <h5>Field Types</h5>
                    {[
                      "Text",
                      "Textarea",
                      "Dropdown",
                      "Checkbox",
                      "Radio",
                      "Number",
                      "Email",
                      "Date",
                      "Time",
                      "DateTime",
                      "File",
                      "Tel",
                      "Image",
                    ].map((type, index) => (
                      <Draggable
                        draggableId={`palette-${type}`}
                        index={index}
                        key={type}
                      >
                        {(prov) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            className="palette-item"
                            onClick={() => {
                              createFieldOfType(type);
                              setShowEditor(true);
                            }}
                          >
                            {type}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              <Droppable droppableId="canvas">
                {(provided) => (
                  <div
                    className="canvas"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <h5>
                      Form Fields (drag from left to add, drag to reorder)
                    </h5>
                    <div className="canvas-list">
                      {fields && fields.length > 0 ? (
                        fields
                          .slice()
                          .sort(
                            (a, b) =>
                              (a.OrderIndex ?? a.orderIndex ?? 0) -
                              (b.OrderIndex ?? b.orderIndex ?? 0),
                          )
                          .map((f, idx) => {
                            const id = f.formFieldId ?? f.FormFieldId ?? idx;
                            return (
                              <Draggable
                                key={`canvas-${id}-${idx}`}
                                draggableId={`canvas-${id}-${idx}`}
                                index={idx}
                              >
                                {(prov, snapshot) => (
                                  <div
                                    ref={prov.innerRef}
                                    {...prov.draggableProps}
                                    {...prov.dragHandleProps}
                                    className={`canvas-item ${snapshot.isDraggingOver || snapshot.isDragging ? "drag-over" : ""}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEdit(f, idx);
                                    }}
                                  >
                                    <div className="drag-handle">≡</div>
                                    <div className="item-body">
                                      <div className="item-label">
                                        {f.Label ?? f.label}
                                      </div>
                                      <div className="item-type">
                                        {f.FieldType ?? f.fieldType}
                                      </div>
                                    </div>
                                    <div className="item-actions">
                                      <button
                                        className="v-link primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEdit(f, idx);
                                        }}
                                      >
                                        <img
                                          className="edit-icon"
                                          src={edit}
                                          alt="Edit"
                                        />
                                      </button>
                                      <button
                                        className="v-link primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteField(f, idx);
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faTrash}
                                          className="delete-icon"
                                        />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })
                      ) : (
                        <div className="no-fields">
                          No fields yet — drag from left to add.
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>
        ) : (
          <div className="preview-panel">
            <div className="form-card preview-form-card">
              <h1 className="form-title">Form Preview</h1>
              <div className="form-description">
                This is a preview of the current form layout.
              </div>
              <form
                className="dynamic-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <p className="form-sub">
                  Please fill out the fields below -{" "}
                  <span className="important">
                    * Indicates required question
                  </span>
                </p>
                {(fields || [])
                  .slice()
                  .sort(
                    (a, b) =>
                      (a.OrderIndex ?? a.orderIndex ?? 0) -
                      (b.OrderIndex ?? b.orderIndex ?? 0),
                  )
                  .map((fieldObj) => {
                    const id = fieldObj.formFieldId ?? fieldObj.FormFieldId;
                    const name =
                      fieldObj.fieldName || fieldObj.FieldName || `field_${id}`;
                    const label = fieldObj.label || fieldObj.Label || name;
                    const type = (
                      fieldObj.fieldType ||
                      fieldObj.FieldType ||
                      "text"
                    ).toLowerCase();
                    const placeholder =
                      fieldObj.placeholder ?? fieldObj.Placeholder ?? "";
                    const required = !!(
                      fieldObj.isRequired ?? fieldObj.IsRequired
                    );
                    const opts = fieldObj.OptionsArray || [];

                    if (type === "textarea") {
                      return (
                        <div className="field" key={id}>
                          <label>
                            {label}
                            {required && <span className="required">*</span>}
                          </label>
                          <textarea
                            placeholder={placeholder}
                            defaultValue={
                              fieldObj.DefaultValue ??
                              fieldObj.defaultValue ??
                              ""
                            }
                            value={formValues[name] || ""}
                            onChange={(e) =>
                              handleChange(name, e.target.value, fieldObj)
                            }
                          />
                          {errors[name] && <div className="error">{errors[name]}</div>}
                        </div>
                      );
                    }

                    if (type === "image") {
                      return (
                        <div className="field" key={id}>
                          <label>
                            {label}
                            {required && <span className="required">*</span>}
                          </label>
                          <img
                            src={baseUrl +
                              (opts[0] &&
                                (opts[0].OptionValue || opts[0].OptionLabel)) ||
                              ""
                            }
                            alt={label}
                            style={{ maxWidth: 300 }}
                          />
                        </div>
                      );
                    }

                    if (type === "file" || type === "upload") {
                      return (
                        <div className="field" key={id}>
                          <label>
                            {label}
                            {required && <span className="required">*</span>}
                          </label>
                          <input
                            type="file"
                            value={formValues[name] || ""}
                            onChange={(e) =>
                              handleChange(name, e.target.value, fieldObj)
                            }
                          />
                          {errors[name] && <div className="error">{errors[name]}</div>}
                        </div>
                      );
                    }

                    if (type === "select" || type === "dropdown") {
                      return (
                        <div className="field" key={id}>
                          <label>
                            {label}
                            {required && <span className="required">*</span>}
                          </label>
                          <select
                            value={formValues[name] || ""}
                            onChange={(e) =>
                              handleChange(name, e.target.value, fieldObj)
                            }
                          >
                            <option value="">Select</option>
                            {opts
                              .sort((a, b) => a.OrderIndex - b.OrderIndex)
                              .map((o) => (
                                <option
                                  key={o.FieldOptionId ?? o.fieldOptionId}
                                  value={o.OptionValue ?? o.OptionLabel}
                                >
                                  {o.OptionLabel ?? o.OptionValue}
                                </option>
                              ))}
                          </select>
                          {errors[name] && <div className="error">{errors[name]}</div>}
                        </div>
                      );
                    }

                    if (type === "radio") {
                      return (
                        <div className="field" key={id}>
                          <label>
                            {label}
                            {required && <span className="required">*</span>}
                          </label>
                          <div className="options-row">
                            {opts
                              .sort((a, b) => a.OrderIndex - b.OrderIndex)
                              .map((o) => (
                                <label
                                  className="option"
                                  key={o.FieldOptionId ?? o.fieldOptionId}
                                >
                                  <input type="radio" name={name} />
                                  <span>{o.OptionLabel ?? o.OptionValue}</span>
                                </label>
                              ))}
                          </div>
                          {errors[name] && <div className="error">{errors[name]}</div>}
                        </div>
                      );
                    }

                    if (type === "checkbox") {
                      if (opts.length > 0) {
                        return (
                          <div className="field" key={id}>
                            <label>
                              {label}
                              {required && <span className="required">*</span>}
                            </label>
                            <div className="options-row">
                              {opts
                                .sort((a, b) => a.OrderIndex - b.OrderIndex)
                                .map((o) => (
                                  <label
                                    className="option"
                                    key={o.FieldOptionId ?? o.fieldOptionId}
                                  >
                                    <input type="checkbox" />
                                    <span>
                                      {o.OptionLabel ?? o.OptionValue}
                                    </span>
                                  </label>
                                ))}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="field" key={id}>
                          <label className="inline">
                            <input type="checkbox" />
                            <span>
                              {label}
                              {required && <span className="required">*</span>}
                            </span>
                          </label>
                          {errors[name] && <div className="error">{errors[name]}</div>}
                        </div>
                      );
                    }

                    const inputType = [
                      "email",
                      "number",
                      "date",
                      "tel",
                      "password",
                    ].includes(type)
                      ? type
                      : "text";
                    return (
                      <div className="field" key={id}>
                        <label>
                          {label}
                          {required && <span className="required">*</span>}
                        </label>
                        <input
                          type={inputType}
                          placeholder={placeholder}
                          defaultValue={
                            fieldObj.DefaultValue ?? fieldObj.defaultValue ?? ""
                          }
                          value={formValues[name] || ""}
                          onChange={(e) =>
                            handleChange(name, e.target.value, fieldObj)
                          }
                        />
                        {errors[name] && <div className="error">{errors[name]}</div>}
                      </div>
                    );
                  })}
                {/* <div className="actions">
                  <button className="submit-btn" type="submit">
                    Check Validation
                  </button>
                </div> */}
              </form>
            </div>
          </div>
        )}

        <Modal
          key={field.FormFieldId || editingIndex}
          isOpen={showEditor}
          closeModal={() => setShowEditor(false)}
          className="modal-2"
        >
          <div className="data">
            <div className="row">
              <div className="add">
                <label>Label</label>
                <Input
                  value={field.Label}
                  onChange={(e) =>
                    setField({ ...field, Label: e.target.value })
                  }
                />
              </div>

              {/* <div className="add">
                <label>Name</label>
                <Input
                  value={field.FieldName}
                  onChange={(e) =>
                    setField({ ...field, FieldName: e.target.value })
                  }
                />
              </div> */}
            </div>

            <div className="row">
              {/* <div className="add">
                <label>Type</label>
                <select
                  disabled
                  value={field.FieldType}
                  onChange={(e) =>
                    setField({ ...field, FieldType: e.target.value })
                  }
                >
                  <option value="Text">Text</option>
                  <option value="Textarea">Textarea</option>
                  <option value="Select">Select</option>
                  <option value="MultiSelect">MultiSelect</option>
                  <option value="Checkbox">Checkbox</option>
                  <option value="Radio">Radio</option>
                  <option value="Image">Image</option>
                </select>
              </div> */}

              {[
                  "text",
                  "textarea",
                  "number",
                  "email",
                  "tel",
                ].includes(
                  (field.FieldType || "").toString().toLowerCase(),
                ) && (
                <div className="add">
                  <label>Placeholder</label>
                  <Input
                    value={field.Placeholder}
                    onChange={(e) =>
                      setField({ ...field, Placeholder: e.target.value })
                    }
                  />
                </div>
              )
            }

              <div className="add">
                <label>Required</label>
                <ToggleSwitch
                  checked={!!field.IsRequired}
                  onChange={(e) => setField({ ...field, IsRequired: e.target.checked })}
                />
              </div>
            </div>

            {/* <div className="row">
              <div className="add">
                <label>Default Value</label>
                <Input
                  value={field.DefaultValue}
                  onChange={(e) =>
                    setField({ ...field, DefaultValue: e.target.value })
                  }
                />
              </div>

              <div className="add">
                  <label>Order Index</label>
                  <Input type="number" value={field.OrderIndex} onChange={(e) => setField({ ...field, OrderIndex: Number(e.target.value) })} />
                </div>
            </div> */}

            <div className="row">
              <div className="add">
                {[
                  "select",
                  "dropdown",
                  "multiselect",
                  "radio",
                  "checkbox",
                  "image",
                ].includes(
                  (field.FieldType || "").toString().toLowerCase(),
                ) && (
                  <>
                    <label>Options</label>
                    <div className="options-editor">
                      <div className="options-list">
                        {field.OptionsArray && field.OptionsArray.length > 0 ? (
                          field.OptionsArray.map((op, oi) => (
                            <div
                              className="option-item"
                              key={op.FieldOptionId || oi}
                            >
                              <div className="option-inputs">
                                <Input
                                  value={op.OptionLabel || ""}
                                  placeholder="Label"
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setField((prev) => {
                                      const next = (
                                        prev.OptionsArray || []
                                      ).map((o, idx) =>
                                        idx === oi
                                          ? {
                                              ...o,
                                              OptionLabel: v,
                                              OrderIndex: idx,
                                            }
                                          : { ...o, OrderIndex: idx },
                                      );
                                      return { ...prev, OptionsArray: next };
                                    });
                                  }}
                                />
                                {(field.FieldType || field.fieldType || "")
                                  .toString()
                                  .toLowerCase() === "image" ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    <Upload
                                      allowedFiles={[
                                        "png",
                                        "jpg",
                                        "jpeg",
                                        "svg",
                                      ]}
                                      onChange={async (file) => {
                                        try {
                                          const formData = new FormData();
                                          formData.append("file", file);

                                          const resp = await postRequest(
                                            "FieldOption/UploadQR",
                                            formData,
                                          );
                                          if (
                                            resp &&
                                            (resp.status === 200 ||
                                              resp.status === 201) &&
                                            resp.data
                                          ) {
                                            const d = resp.data;
                                            // try common property names for returned URL
                                            const url =
                                              d.blob.name ||
                                              null;

                                            if (url) {
                                              setField((prev) => {
                                                const next = (
                                                  prev.OptionsArray || []
                                                ).map((o, idx) =>
                                                  idx === oi
                                                    ? {
                                                        ...o,
                                                        OptionValue: url,
                                                        OptionLabel:
                                                          o.OptionLabel ||
                                                          file.name,
                                                        OrderIndex: idx,
                                                      }
                                                    : { ...o, OrderIndex: idx },
                                                );
                                                return {
                                                  ...prev,
                                                  OptionsArray: next,
                                                };
                                              });
                                            } else {
                                              NotificationManager.error(
                                                "Upload succeeded but no URL returned",
                                              );
                                            }
                                          } else {
                                            console.error(
                                              "Upload failed",
                                              resp,
                                            );
                                            NotificationManager.error(
                                              "Image upload failed",
                                            );
                                          }
                                        } catch (err) {
                                          console.error("Upload error", err);
                                          NotificationManager.error(
                                            "Image upload failed",
                                          );
                                        }
                                      }}
                                    />
                                    <div style={{ width: 80, height: 56 }}>
                                      {op.OptionValue ? (
                                        // preview thumbnail
                                        <img
                                          src={baseUrl + op.OptionValue}
                                          alt={op.OptionLabel}
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                          }}
                                        />
                                      ) : (
                                        <div className="no-thumb">No image</div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <Input
                                    value={op.OptionValue || ""}
                                    placeholder="Value"
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setField((prev) => {
                                        const next = (
                                          prev.OptionsArray || []
                                        ).map((o, idx) =>
                                          idx === oi
                                            ? {
                                                ...o,
                                                OptionValue: v,
                                                OrderIndex: idx,
                                              }
                                            : { ...o, OrderIndex: idx },
                                        );
                                        return { ...prev, OptionsArray: next };
                                      });
                                    }}
                                  />
                                )}
                              </div>
                              <div className="option-actions">
                                {(field.FieldType || field.fieldType || "")
                                  .toString()
                                  .toLowerCase() !== "image" && (
                                  <>
                                    <Anchor
                                      title="Move Up"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (oi === 0) return;
                                        setField((prev) => {
                                          const arr = [
                                            ...(prev.OptionsArray || []),
                                          ];
                                          const [item] = arr.splice(oi, 1);
                                          arr.splice(oi - 1, 0, item);
                                          return {
                                            ...prev,
                                            OptionsArray: arr.map((o, idx) => ({
                                              ...o,
                                              OrderIndex: idx,
                                            })),
                                          };
                                        });
                                      }}
                                      type="primary"
                                    >
                                      <FontAwesomeIcon
                                        icon={faArrowUp}
                                        className="arrow-icon"
                                      />
                                    </Anchor>

                                    <Anchor
                                      title="Move Down"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const len = (field.OptionsArray || [])
                                          .length;
                                        if (oi === len - 1) return;
                                        setField((prev) => {
                                          const arr = [
                                            ...(prev.OptionsArray || []),
                                          ];
                                          const [item] = arr.splice(oi, 1);
                                          arr.splice(oi + 1, 0, item);
                                          return {
                                            ...prev,
                                            OptionsArray: arr.map((o, idx) => ({
                                              ...o,
                                              OrderIndex: idx,
                                            })),
                                          };
                                        });
                                      }}
                                      type="primary"
                                    >
                                      <FontAwesomeIcon
                                        icon={faArrowDown}
                                        className="arrow-icon"
                                      />
                                    </Anchor>
                                  </>
                                )}

                                <Anchor
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setField((prev) => {
                                      const next = (prev.OptionsArray || [])
                                        .filter((_, i) => i !== oi)
                                        .map((o, idx) => ({
                                          ...o,
                                          OrderIndex: idx,
                                        }));
                                      return { ...prev, OptionsArray: next };
                                    });
                                  }}
                                  type="primary"
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="delete-icon"
                                  />
                                </Anchor>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-options">No options yet</div>
                        )}
                      </div>
                      {(((field.FieldType || field.fieldType || "")
                        .toString()
                        .toLowerCase() === "image" &&
                        field.OptionsArray?.length === 0) ||
                        (field.FieldType || field.fieldType || "")
                          .toString()
                          .toLowerCase() !== "image") && (
                        <div className="options-add">
                          <Button
                            onClick={() => {
                              setField((prev) => {
                                const next = [
                                  ...(prev.OptionsArray || []),
                                  {
                                    FieldOptionId: 0,
                                    FormFieldId: prev.FormFieldId ?? 0,
                                    OptionLabel: "",
                                    OptionValue: "",
                                    OrderIndex: (prev.OptionsArray || [])
                                      .length,
                                  },
                                ];
                                return { ...prev, OptionsArray: next };
                              });
                            }}
                          >
                            {(field.FieldType || field.fieldType || "")
                              .toString()
                              .toLowerCase() === "image"
                              ? "Add Image"
                              : "Add Option"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* <div className="row">
              <div className="add description">
                <label>Validation JSON</label>
                <textarea
                  value={field.ValidationJson}
                  onChange={(e) =>
                    setField({ ...field, ValidationJson: e.target.value })
                  }
                />
              </div>
            </div> */}

            <div className="actions-row">
              <Button onClick={localSaveField}>
                {editing ? "Update" : "Save"}
              </Button>
              <Button type="primary" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={confirmModal.isOpen}
          closeModal={() =>
            setConfirmModal({ isOpen: false, message: "", onConfirm: null })
          }
          className="modal-2"
        >
          <div className="circle">!</div>
          <div>
            <h4>Are you sure?</h4>
            <p>{confirmModal.message}</p>
            <div className="v-modal-button-wrapper">
              <Button
                onClick={async () => {
                  if (confirmModal.onConfirm) await confirmModal.onConfirm();
                  setConfirmModal({
                    isOpen: false,
                    message: "",
                    onConfirm: null,
                  });
                }}
              >
                Yes
              </Button>
              <Button
                onClick={() =>
                  setConfirmModal({
                    isOpen: false,
                    message: "",
                    onConfirm: null,
                  })
                }
                type="primary"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </MainA>
  );
};

export default FormFields;
